import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Stethoscope, ArrowLeft, Shield, CheckCircle, AlertCircle, AlertTriangle, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ComplianceHistoryPage() {
  const { doctorId } = useParams();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReports();
  }, [doctorId]);

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API}/compliance/reports/${doctorId}`);
      setReports(response.data.reports || []);
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast.error('Failed to load compliance reports');
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'low': return 'bg-green-100 text-green-700 border-green-200';
      case 'medium': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'high': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getRiskIcon = (level) => {
    switch (level?.toLowerCase()) {
      case 'low': return <CheckCircle className="w-5 h-5" strokeWidth={1.5} />;
      case 'medium': return <AlertTriangle className="w-5 h-5" strokeWidth={1.5} />;
      case 'high': return <AlertCircle className="w-5 h-5" strokeWidth={1.5} />;
      default: return <Shield className="w-5 h-5" strokeWidth={1.5} />;
    }
  };

  const downloadReport = (report) => {
    const reportText = `
COMPLIANCE REPORT
=================

Script: ${report.script_topic || 'Unnamed Script'}
Country: ${report.country}
Risk Level: ${report.risk_level}
Date: ${new Date(report.created_at).toLocaleString()}

VIOLATIONS:
${report.violations?.map(v => `- ${v}`).join('\n') || 'None'}

MISSING ELEMENTS:
${report.missing_elements?.map(m => `- ${m}`).join('\n') || 'None'}

SUGGESTIONS:
${report.suggestions?.map(s => `- ${s}`).join('\n') || 'None'}

${report.approved_version ? `
APPROVED VERSION:
Hook: ${report.approved_version.hook || ''}
Explanation: ${report.approved_version.explanation || ''}
CTA: ${report.approved_version.cta || ''}
Disclaimer: ${report.approved_version.disclaimer || ''}
` : ''}
    `.trim();

    const blob = new Blob([reportText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `compliance_report_${report.id}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Report downloaded!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#2C5F44] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#5C7063]">Loading compliance reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] grain-texture">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center">
          <Button
            onClick={() => navigate(`/dashboard/${doctorId}`)}
            variant="ghost"
            size="sm"
            className="rounded-full mr-4"
            data-testid="compliance-history-back-btn"
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
          </Button>
          <Stethoscope className="w-8 h-8 text-[#2C5F44] mr-2" strokeWidth={1.5} />
          <span className="text-2xl font-bold text-[#2C5F44]" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Compliance History
          </span>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold text-[#1C2A21] mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Compliance Reports
          </h1>
          <p className="text-lg text-[#5C7063]">
            {reports.length} report{reports.length !== 1 ? 's' : ''} saved
          </p>
        </motion.div>

        {reports.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center py-20"
          >
            <div className="w-24 h-24 bg-[#82A08B]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Shield className="w-12 h-12 text-[#2C5F44]" strokeWidth={1.5} />
            </div>
            <h2 className="text-3xl font-bold text-[#1C2A21] mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
              No Compliance Reports Yet
            </h2>
            <p className="text-lg text-[#5C7063] mb-8 max-w-2xl mx-auto">
              Compliance reports are automatically saved when you check scripts for compliance
            </p>
            <Button
              onClick={() => navigate(`/script/${doctorId}`)}
              className="button-pill bg-[#2C5F44] hover:bg-[#244b36] text-white rounded-full px-8 py-6 text-lg"
            >
              Create & Check Script
            </Button>
          </motion.div>
        ) : (
          <div className="space-y-6">
            {reports.map((report, index) => (
              <motion.div
                key={report.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="bg-white rounded-3xl p-8 border border-[#2C5F44]/10"
                data-testid={`compliance-report-${index}`}
              >
                <div className="flex justify-between items-start mb-6">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-[#1C2A21] mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
                      {report.script_topic || 'Unnamed Script'}
                    </h3>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-[#5C7063]">
                        {new Date(report.created_at).toLocaleDateString()}
                      </span>
                      <Badge className="bg-blue-100 text-blue-700 rounded-full">
                        {report.country}
                      </Badge>
                    </div>
                  </div>
                  <div className={`flex items-center gap-2 px-4 py-2 rounded-full border ${getRiskColor(report.risk_level)}`}>
                    {getRiskIcon(report.risk_level)}
                    <span className="font-semibold">{report.risk_level} Risk</span>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  {report.violations && report.violations.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-red-700 mb-2">Violations Found</h4>
                      <ul className="space-y-1">
                        {report.violations.map((v, i) => (
                          <li key={i} className="text-sm text-red-600 flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                            <span>{v}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {report.suggestions && report.suggestions.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-[#2C5F44] mb-2">Suggestions</h4>
                      <ul className="space-y-1">
                        {report.suggestions.map((s, i) => (
                          <li key={i} className="text-sm text-[#5C7063] flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#2C5F44]" strokeWidth={1.5} />
                            <span>{s}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <Button
                  onClick={() => downloadReport(report)}
                  variant="outline"
                  size="sm"
                  className="rounded-full border-[#2C5F44]/20"
                  data-testid={`download-report-${index}`}
                >
                  <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Download Report
                </Button>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
